// src/components/CompanyQuestionsEditor.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import {
  CompanyQuestion,
  QuestionsJson,
  Product,
  Question,
  QuestionConditions,
  QuestionTriggers,
} from '../types/companyQuestions';
import QuestionAdvancedSettings from './QuestionAdvancedSettings';

interface CompanyQuestionsEditorProps {
  token: string;
}

const CompanyQuestionsEditor: React.FC<CompanyQuestionsEditorProps> = ({ token }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id; // If id exists, it's edit mode

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [knowledgeBases, setKnowledgeBases] = useState<any[]>([]);
  const [selectedKbId, setSelectedKbId] = useState<number | ''>('');
  const [isActive, setIsActive] = useState(true);
  const [questionsJson, setQuestionsJson] = useState<QuestionsJson>({
    company_name: '',
    products: [],
    global_settings: {
      qualification_scoring: {
        required_weight: 0.7,
        business_impact_weight: 0.3,
      },
    },
  });

  const [expandedProducts, setExpandedProducts] = useState<Set<number>>(new Set());
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchKnowledgeBases();
    if (isEditMode && id) {
      fetchCompanyQuestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchKnowledgeBases = async () => {
    try {
      const response = await api.get('/api/kb/list', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKnowledgeBases(response.data.knowledgeBases || []);
    } catch (err) {
      console.error('Error fetching knowledge bases:', err);
    }
  };

  const fetchCompanyQuestion = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/company-questions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const cq: CompanyQuestion = response.data.companyQuestion;
      setSelectedKbId(cq.kbId);
      setIsActive(cq.isActive);
      setQuestionsJson(cq.questionsJson);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to fetch company question');
      navigate('/company-questions');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedKbId) {
      alert('Please select a Knowledge Base');
      return;
    }

    if (!questionsJson.company_name.trim()) {
      alert('Please enter a company name');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        kbId: selectedKbId,
        questionsJson,
        isActive,
      };

      if (isEditMode) {
        await api.put(`/company-questions/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert('Company question updated successfully');
      } else {
        await api.post('/company-questions', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert('Company question created successfully');
      }
      navigate('/company-questions');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save company question');
      console.error('Error saving:', err);
    } finally {
      setSaving(false);
    }
  };

  const addProduct = () => {
    const newProduct: Product = {
      id: `product_${Date.now()}`,
      name: 'New Product',
      questions: [],
      qualification_threshold: 0.8,
      show_progress: true,
    };
    setQuestionsJson({
      ...questionsJson,
      products: [...questionsJson.products, newProduct],
    });
  };

  const updateProduct = (index: number, updates: Partial<Product>) => {
    const updatedProducts = [...questionsJson.products];
    updatedProducts[index] = { ...updatedProducts[index], ...updates };
    setQuestionsJson({ ...questionsJson, products: updatedProducts });
  };

  const deleteProduct = (index: number) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    const updatedProducts = questionsJson.products.filter((_, i) => i !== index);
    setQuestionsJson({ ...questionsJson, products: updatedProducts });
  };

  const addQuestion = (productIndex: number) => {
    const newQuestion: Question = {
      id: `question_${Date.now()}`,
      text: 'New Question',
      type: 'single_choice',
      category: 'general',
      priority: 5,
      required: false,
      business_impact: 5,
      conditions: null,
    };
    const updatedProducts = [...questionsJson.products];
    updatedProducts[productIndex].questions.push(newQuestion);
    setQuestionsJson({ ...questionsJson, products: updatedProducts });
  };

  const updateQuestion = (
    productIndex: number,
    questionIndex: number,
    updates: Partial<Question>
  ) => {
    const updatedProducts = [...questionsJson.products];
    updatedProducts[productIndex].questions[questionIndex] = {
      ...updatedProducts[productIndex].questions[questionIndex],
      ...updates,
    };
    setQuestionsJson({ ...questionsJson, products: updatedProducts });
  };

  const deleteQuestion = (productIndex: number, questionIndex: number) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    const updatedProducts = [...questionsJson.products];
    updatedProducts[productIndex].questions = updatedProducts[productIndex].questions.filter(
      (_, i) => i !== questionIndex
    );
    setQuestionsJson({ ...questionsJson, products: updatedProducts });
  };

  const toggleProductExpand = (index: number) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedProducts(newExpanded);
  };

  const toggleQuestionExpand = (key: string) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedQuestions(newExpanded);
  };

  if (loading) {
    return <div style={{ padding: '50px', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => navigate('/company-questions')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginRight: '10px',
          }}
        >
          ← Back
        </button>
        <h2 style={{ display: 'inline' }}>
          {isEditMode ? 'Edit Company Questions' : 'Create Company Questions'}
        </h2>
      </div>

      {/* Basic Settings */}
      <div
        style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '5px',
          marginBottom: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <h3>Basic Settings</h3>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Knowledge Base *
          </label>
          <select
            value={selectedKbId}
            onChange={(e) => setSelectedKbId(Number(e.target.value))}
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: '5px',
              border: '1px solid #ced4da',
            }}
          >
            <option value="">Select a Knowledge Base</option>
            {knowledgeBases.map((kb) => (
              <option key={kb.id} value={kb.id}>
                {kb.name} (ID: {kb.id})
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Company Name *
          </label>
          <input
            type="text"
            value={questionsJson.company_name}
            onChange={(e) =>
              setQuestionsJson({ ...questionsJson, company_name: e.target.value })
            }
            placeholder="Enter company name"
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: '5px',
              border: '1px solid #ced4da',
            }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            <span style={{ fontWeight: 'bold' }}>Active</span>
          </label>
        </div>
      </div>

      {/* Products Section */}
      <div
        style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '5px',
          marginBottom: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3>Products ({questionsJson.products.length})</h3>
          <button
            onClick={addProduct}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
            }}
          >
            + Add Product
          </button>
        </div>

        {questionsJson.products.map((product, productIndex) => (
          <div
            key={productIndex}
            style={{
              border: '1px solid #dee2e6',
              borderRadius: '5px',
              marginBottom: '15px',
              overflow: 'hidden',
            }}
          >
            {/* Product Header */}
            <div
              style={{
                backgroundColor: '#f8f9fa',
                padding: '12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={() => toggleProductExpand(productIndex)}
            >
              <h4 style={{ margin: 0 }}>
                {expandedProducts.has(productIndex) ? '▼' : '▶'} {product.name} (
                {product.questions.length} questions)
              </h4>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteProduct(productIndex);
                }}
                style={{
                  padding: '5px 10px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                }}
              >
                Delete
              </button>
            </div>

            {/* Product Details */}
            {expandedProducts.has(productIndex) && (
              <div style={{ padding: '15px' }}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Product ID
                  </label>
                  <input
                    type="text"
                    value={product.id}
                    onChange={(e) => updateProduct(productIndex, { id: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '5px',
                      border: '1px solid #ced4da',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Product Name
                  </label>
                  <input
                    type="text"
                    value={product.name}
                    onChange={(e) => updateProduct(productIndex, { name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '5px',
                      border: '1px solid #ced4da',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Qualification Threshold
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={product.qualification_threshold}
                    onChange={(e) =>
                      updateProduct(productIndex, {
                        qualification_threshold: parseFloat(e.target.value),
                      })
                    }
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '5px',
                      border: '1px solid #ced4da',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={product.show_progress !== false}
                      onChange={(e) =>
                        updateProduct(productIndex, { show_progress: e.target.checked })
                      }
                      style={{ marginRight: '8px' }}
                    />
                    <span style={{ fontWeight: 'bold' }}>Show Progress for this Product</span>
                  </label>
                </div>

                {/* Questions */}
                <div style={{ marginTop: '20px' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '10px',
                    }}
                  >
                    <h4>Questions ({product.questions.length})</h4>
                    <button
                      onClick={() => addQuestion(productIndex)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                      }}
                    >
                      + Add Question
                    </button>
                  </div>

                  {product.questions.map((question, questionIndex) => {
                    const questionKey = `${productIndex}-${questionIndex}`;
                    return (
                      <QuestionEditor
                        key={questionIndex}
                        question={question}
                        questionKey={questionKey}
                        allQuestions={product.questions}
                        expanded={expandedQuestions.has(questionKey)}
                        onToggle={() => toggleQuestionExpand(questionKey)}
                        onUpdate={(updates) =>
                          updateQuestion(productIndex, questionIndex, updates)
                        }
                        onDelete={() => deleteQuestion(productIndex, questionIndex)}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Save Button */}
      <div style={{ textAlign: 'center', marginTop: '30px' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '12px 40px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
          }}
        >
          {saving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
        </button>
      </div>
    </div>
  );
};

// Question Editor Component
interface QuestionEditorProps {
  question: Question;
  questionKey: string;
  allQuestions: Question[];
  expanded: boolean;
  onToggle: () => void;
  onUpdate: (updates: Partial<Question>) => void;
  onDelete: () => void;
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({
  question,
  allQuestions,
  expanded,
  onToggle,
  onUpdate,
  onDelete,
}) => {
  return (
    <div
      style={{
        border: '1px solid #e0e0e0',
        borderRadius: '3px',
        marginBottom: '10px',
        overflow: 'hidden',
      }}
    >
      {/* Question Header */}
      <div
        style={{
          backgroundColor: '#f1f3f5',
          padding: '10px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={onToggle}
      >
        <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
          {expanded ? '▼' : '▶'} {question.text}
        </span>
        <div>
          <span
            style={{
              marginRight: '10px',
              padding: '3px 8px',
              backgroundColor: '#e7f3ff',
              borderRadius: '3px',
              fontSize: '12px',
            }}
          >
            {question.type}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            style={{
              padding: '3px 8px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            Delete
          </button>
        </div>
      </div>

      {/* Question Details */}
      {expanded && (
        <div style={{ padding: '15px', backgroundColor: '#fafafa' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>
                Question ID
              </label>
              <input
                type="text"
                value={question.id}
                onChange={(e) => onUpdate({ id: e.target.value })}
                style={{
                  width: '100%',
                  padding: '6px',
                  borderRadius: '3px',
                  border: '1px solid #ced4da',
                  fontSize: '13px',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>
                Type
              </label>
              <select
                value={question.type}
                onChange={(e) =>
                  onUpdate({ type: e.target.value as Question['type'] })
                }
                style={{
                  width: '100%',
                  padding: '6px',
                  borderRadius: '3px',
                  border: '1px solid #ced4da',
                  fontSize: '13px',
                }}
              >
                <option value="single_choice">Single Choice</option>
                <option value="multi_choice">Multi Choice</option>
                <option value="number">Number</option>
                <option value="text">Text</option>
              </select>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>
                Question Text
              </label>
              <input
                type="text"
                value={question.text}
                onChange={(e) => onUpdate({ text: e.target.value })}
                style={{
                  width: '100%',
                  padding: '6px',
                  borderRadius: '3px',
                  border: '1px solid #ced4da',
                  fontSize: '13px',
                }}
              />
            </div>

            {(question.type === 'single_choice' || question.type === 'multi_choice') && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 'bold' }}>
                  Options
                </label>

                {/* Existing Options */}
                {question.options && question.options.length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    {question.options.map((option, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '8px',
                          padding: '8px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '5px',
                          border: '1px solid #dee2e6',
                        }}
                      >
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => {
                            const newOptions = [...(question.options || [])];
                            newOptions[idx] = e.target.value;
                            onUpdate({ options: newOptions });
                          }}
                          style={{
                            flex: 1,
                            padding: '6px',
                            borderRadius: '3px',
                            border: '1px solid #ced4da',
                            fontSize: '13px',
                            marginRight: '8px',
                          }}
                        />
                        <button
                          onClick={() => {
                            const newOptions = question.options?.filter((_, i) => i !== idx);
                            onUpdate({ options: newOptions });
                          }}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '12px',
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Option */}
                <button
                  onClick={() => {
                    const newOptions = [...(question.options || []), ''];
                    onUpdate({ options: newOptions });
                  }}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: '12px',
                  }}
                >
                  + Add Option
                </button>
              </div>
            )}

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>
                Category
              </label>
              <input
                type="text"
                value={question.category}
                onChange={(e) => onUpdate({ category: e.target.value })}
                style={{
                  width: '100%',
                  padding: '6px',
                  borderRadius: '3px',
                  border: '1px solid #ced4da',
                  fontSize: '13px',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>
                Priority (1-10)
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={question.priority}
                onChange={(e) => onUpdate({ priority: parseInt(e.target.value) })}
                style={{
                  width: '100%',
                  padding: '6px',
                  borderRadius: '3px',
                  border: '1px solid #ced4da',
                  fontSize: '13px',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>
                Business Impact (1-10)
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={question.business_impact}
                onChange={(e) => onUpdate({ business_impact: parseInt(e.target.value) })}
                style={{
                  width: '100%',
                  padding: '6px',
                  borderRadius: '3px',
                  border: '1px solid #ced4da',
                  fontSize: '13px',
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                <input
                  type="checkbox"
                  checked={question.required}
                  onChange={(e) => onUpdate({ required: e.target.checked })}
                  style={{ marginRight: '6px' }}
                />
                Required
              </label>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>
                Explanation (Optional)
              </label>
              <textarea
                value={question.explanation || ''}
                onChange={(e) => onUpdate({ explanation: e.target.value })}
                rows={2}
                style={{
                  width: '100%',
                  padding: '6px',
                  borderRadius: '3px',
                  border: '1px solid #ced4da',
                  fontSize: '13px',
                }}
              />
            </div>

            {/* Qualification Weight (optional) */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>
                Qualification Weight (Optional, 0-1)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="1"
                value={question.qualification_weight ?? ''}
                onChange={(e) =>
                  onUpdate({
                    qualification_weight: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                placeholder="e.g., 0.3"
                style={{
                  width: '100%',
                  padding: '6px',
                  borderRadius: '3px',
                  border: '1px solid #ced4da',
                  fontSize: '13px',
                }}
              />
              <small style={{ fontSize: '11px', color: '#666' }}>
                Used for lead qualification scoring
              </small>
            </div>
          </div>

          {/* Advanced Settings (Triggers, Conditions, Validation) */}
          <QuestionAdvancedSettings
            question={question}
            allQuestions={allQuestions}
            onUpdate={onUpdate}
          />
        </div>
      )}
    </div>
  );
};

export default CompanyQuestionsEditor;
